import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BoundedContent,
    Box,
    Button,
    Column,
    FormField,
    FormInputGroup,
    GridContainer,
    Icon,
    IconButton,
    responsive,
    Row,
    Spinner,
    StandardForm,
    TextInput,
    TextInputWithButtonInset,
    Typography,
} from '@vp/swan';
import { useLogger } from '@vp/ubik-context';
import { useOnUserIdentityUpdate } from '../../../../auth';
import BusinessNamesGrid from '../../../../components/BusinessNamesGrid/BusinessNamesGrid';
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage';
import FormErrorWrapper from '../../../../components/FormErrorWrapper/FormErrorWrapper';
import LaunchMyBusinessTools from '../../../../components/LaunchMyBusinessTools/LaunchMyBusinessTools';
import { BusinessToolKey } from '../../../../components/LaunchMyBusinessTools/LaunchMyBusinessToolsConfig';
import { sanitiseLocale } from '../../../../i18n/config';
import { useTrack } from '../../../../utils/analytics';
import { useEnvContext } from '../../../../utils/environmentContext';
import updateSearchParams from '../../../../utils/updateSearchParams';
import type { Logger } from '@vp/ubik-logging';
import type { Identity } from '../../../../auth/useOnUserIdentityUpdate';

declare global {
    interface Window {
        _kiq: Array<string | object>;
    }
}

export const maxKeywordInputLength = 300;

export type BusinessName = {
    name: string;
    description: string;
};

interface BusinessNameResultsInterface {
    businessNames: BusinessName[];
    industryKeywords?: string[];
    hasResultsError: boolean;
}

interface BusinessNameResultsProp {
    inputKeyword: string;
}

const ResponsiveTypography = responsive(Typography);

const BusinessNameResults = ({ inputKeyword }: BusinessNameResultsProp) => {
    const [searchInput, setSearchInput] = useState(inputKeyword);
    const [businessNamesList, setBusinessNamesList] = useState<BusinessName[]>([]);
    const [industryKeywords, setIndustryKeywords] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [formErrorMessage, setFormErrorMessage] = useState('');
    const [resultsError, setResultsError] = useState(false);
    const isInitialLoad = isLoading && businessNamesList.length === 0;
    const textInputRef = useRef<HTMLInputElement>(null);

    const identity: Identity = useOnUserIdentityUpdate();
    const track = useTrack();
    const { i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const { ENV_API_BASE_URL } = useEnvContext();
    const logger: Logger = useLogger();

    async function generateBusinessNames(
        accessToken: string,
    ): Promise<BusinessNameResultsInterface> {
        try {
            const apiURL = `${ENV_API_BASE_URL}/api/generate-business-names`;
            const options: RequestInit = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ keywords: searchInput }),
            };
            const response = await fetch(apiURL, options);

            if (!response.ok) {
                logger.error({
                    name: 'BNG: Failed request to generate-business-names',
                    message: `Request to ${apiURL} failed to fetch business names. Status: ${response.status}.`,
                    logData: {
                        input: searchInput,
                        response: JSON.stringify(response.body),
                    },
                });
            } else {
                const data = await response.json();
                const businessNames = data.output.businessNames;
                if (!businessNames.length) {
                    logger.error({
                        name: 'BNG: No Business Names',
                        message: 'No business names at client side.',
                        logData: {
                            input: searchInput,
                        },
                    });
                }
                return {
                    businessNames,
                    industryKeywords: data.output.industryKeywords,
                    hasResultsError: false,
                };
            }
        } catch (e) {
            logger.error({
                name: 'BNG: Error fetching business names',
                message: (e as Error).message,
                logData: {
                    input: searchInput,
                },
            });
        }

        track('Business Name Generator Api Error', {
            pageSection: 'Results',
            category: 'Results',
            label: 'Content:Results',
            eventDetail: '/business-name-generator;/business-name-generator;Results;apiError',
            navigationDetail: 'apiError',
            query: searchInput,
        });

        return { businessNames: [], hasResultsError: true };
    }

    useEffect(() => {
        if (identity.hasIdentityError) {
            setResultsError(true);
            setIsLoading(false);
            return;
        }

        if (!identity.accessToken) {
            return;
        }

        // If your useEffect fetches something, the cleanup function should ignore its result
        // Reference: https://react.dev/learn/synchronizing-with-effects#fetching-data
        let ignore = false;

        (async function setInitialBusinessNames() {
            setIsLoading(true);
            const { businessNames, industryKeywords, hasResultsError } =
                await generateBusinessNames(identity.accessToken);

            if (!ignore) {
                setResultsError(hasResultsError);
                setBusinessNamesList(businessNames);
                if (industryKeywords) {
                    setIndustryKeywords(industryKeywords);
                }
                setIsLoading(false);
                trackQueryResolved(businessNames, 'Hero', 'showNameIdeas');

                if (!businessNames.length) {
                    return;
                }

                // Show the Qualaroo poll after the business names are fetched and available
                if (!Object.prototype.hasOwnProperty.call(window, '_kiq')) {
                    logger.error({
                        name: 'BNG: Qualaroo library not found',
                        message: 'window._kiq is undefined',
                        logData: {
                            input: searchInput,
                            businessNames: JSON.stringify(businessNames),
                        },
                    });
                    return;
                }

                window._kiq.push([
                    'set',
                    {
                        event: 'Business Names Fetched',
                        query: inputKeyword,
                        results: businessNames.map(({ name }) => name).join(', '),
                    },
                ]);
            }
        })();

        return () => {
            ignore = true;
        };
    }, [identity]);

    function trackQuerySubmitted() {
        track('Query Submitted On Business Name Generator', {
            pageSection: 'Results',
            category: 'Business Name Generator',
            label: 'Content:Results',
            eventDetail: '/business-name-generator;/business-name-generator;Results;formSubmit',
            navigationDetail: 'formSubmit',
            query: searchInput,
        });
    }

    function trackQueryResolved(
        businessNames: BusinessName[],
        pageSection: string,
        navigationDetail: string,
    ) {
        track('Query Resolved On Business Name Generator', {
            pageSection,
            category: 'Business Name Generator',
            label: `Content:${pageSection}`,
            eventDetail: `/business-name-generator;/business-name-generator;${pageSection};${navigationDetail}`,
            navigationDetail,
            query: searchInput,
            result: businessNames.map((businessName) => businessName.name),
        });
    }

    function trackShowMoreButton() {
        track('Navigation Clicked', {
            pageSection: 'GeneratedNamesGrid',
            category: 'Business Name Generator',
            label: 'Content:GeneratedNamesGrid',
            eventDetail:
                '/business-name-generator;/business-name-generator;GeneratedNamesGrid;showMore',
            navigationDetail: 'showMore',
        });
    }

    const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (searchInput.trim().length === 0) {
            setFormErrorMessage('Please describe your business to generate name ideas.');
            // Move focus to the invalid input and interrupt screen reader if button hit mid-announcement.
            textInputRef.current?.focus();
            return;
        }

        if (formErrorMessage) {
            textInputRef.current?.focus();
            return;
        }

        if (identity.hasIdentityError) {
            setResultsError(true);
            return;
        }

        updateSearchParams(searchInput.trim());
        setIsLoading(true);
        trackQuerySubmitted();
        setBusinessNamesList([]);

        const { businessNames, industryKeywords, hasResultsError } = await generateBusinessNames(
            identity.accessToken,
        );
        trackQueryResolved(businessNames, 'Results', 'formSubmit');
        setResultsError(hasResultsError);
        setBusinessNamesList(businessNames);
        if (industryKeywords) {
            setIndustryKeywords(industryKeywords);
        }
        setIsLoading(false);
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.length > maxKeywordInputLength) {
            setFormErrorMessage(`Maximum ${maxKeywordInputLength} character limit`);
        } else {
            setFormErrorMessage('');
            setSearchInput(value);
        }
    };

    const handleOnShowMore = async () => {
        if (identity.hasIdentityError) {
            setResultsError(true);
            return;
        }

        setIsLoading(true);
        trackShowMoreButton();

        const { businessNames, hasResultsError } = await generateBusinessNames(
            identity.accessToken,
        );
        trackQueryResolved(businessNames, 'GeneratedNamesGrid', 'showMore');
        setResultsError(hasResultsError);
        setBusinessNamesList((prevList) => [...prevList, ...businessNames]);
        setIsLoading(false);
    };

    const businessToolKeys = useMemo(() => {
        const tools: BusinessToolKey[] = [
            'logomaker',
            'brandKit',
            'wixWebsites',
            'googleWorkspace',
            'socialMediaTemplates',
            'design',
        ];
        if (locale === 'en-US') {
            tools.unshift('swyftFilings');
        }
        return tools;
    }, [locale]);

    return (
        <>
            <Box
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                }}
                bgc="strong"
            >
                <BoundedContent>
                    <GridContainer>
                        <Row style={{ justifyContent: 'center' }}>
                            <Column
                                spanXs={12}
                                spanSm={12}
                                spanMd={8}
                                span={6}
                                paddingTop={7}
                                style={{ paddingBottom: '7px' }}
                            >
                                <StandardForm onSubmit={handleOnSubmit}>
                                    <FormField>
                                        <FormInputGroup margin={0}>
                                            <TextInputWithButtonInset>
                                                <TextInput
                                                    value={searchInput}
                                                    onChange={handleOnChange}
                                                    placeholder="Cozy neighborhood café serving artisanal coffee and homemade pastries"
                                                    aria-label="Enter keywords to describe your business"
                                                    aria-required
                                                    aria-invalid={!!formErrorMessage}
                                                    aria-errormessage={
                                                        formErrorMessage
                                                            ? 'keywords-error'
                                                            : undefined
                                                    }
                                                    name="keywordDescription"
                                                    ref={textInputRef}
                                                />
                                                <IconButton
                                                    disabled={isLoading}
                                                    loading={isLoading}
                                                    type="submit"
                                                    tooltip="Generate names"
                                                    aria-label="Generate business name ideas"
                                                >
                                                    <Icon iconType="magic" />
                                                </IconButton>
                                            </TextInputWithButtonInset>
                                            <FormErrorWrapper
                                                errorMessage={formErrorMessage}
                                                errorId="keywords-error"
                                            />
                                        </FormInputGroup>
                                    </FormField>
                                </StandardForm>
                            </Column>
                        </Row>
                    </GridContainer>
                </BoundedContent>
            </Box>
            {resultsError && !isLoading ? (
                <ErrorMessage message="Please update your prompt or give it another try in a few minutes." />
            ) : (
                <BoundedContent marginTop="between-subsections" marginBottom="7">
                    <BusinessNamesGrid
                        businessNames={businessNamesList}
                        industryKeywords={industryKeywords}
                        isLoading={isLoading}
                        trackingEventDetails={{
                            category: 'Business Name Generator',
                            pageSection: 'GeneratedNamesGrid',
                        }}
                    />
                    {!isInitialLoad && (
                        <Box textAlign="center">
                            <Button
                                skin="primary"
                                disabled={isLoading}
                                iconPosition="right"
                                onClick={handleOnShowMore}
                            >
                                {isLoading && <Spinner size="tiny" accessibleText="Show more" />}
                                Show more
                            </Button>
                        </Box>
                    )}
                </BoundedContent>
            )}
            <BoundedContent marginTop="between-subsections" marginBottom="between-sections">
                <ResponsiveTypography xs={{ textAlign: 'left' }} sm={{ textAlign: 'center' }}>
                    These business names are AI-generated suggestions. We cannot guarantee that they
                    are all available for registration.
                </ResponsiveTypography>
            </BoundedContent>
            <BoundedContent marginTop="between-subsections" marginBottom="between-sections">
                <LaunchMyBusinessTools
                    header="Got a new business idea? We’ve got you covered."
                    subheader="From naming your business to seeing it on a logo and a slick website, turn your idea into a reality with our digital and design tools. Start creating your business essentials all in one place so you can hit the ground running."
                    businessTools={businessToolKeys}
                    trackingCategory="Business Name Generator"
                />
            </BoundedContent>
        </>
    );
};

export default BusinessNameResults;
